import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
  ShieldCheck,
  History,
  Monitor,
  Smartphone,
  Tablet,
  LogOut,
  Clock,
  Globe,
  AlertCircle,
  Activity,
  User,
  MapPin,
  ChevronRight
} from "lucide-react";
import { Breadcrumb } from "../Breadcrumb";
import API from "../api/api01";
import { toast } from "sonner";
import { format } from "date-fns";
import { cn } from "../ui/utils";

interface Session {
  id: string;
  ip_address: string;
  user_agent: string;
  device_type: string;
  is_active: boolean;
  last_active: string;
  created_at: string;
  user_name?: string;
  user_role?: string;
  user_email?: string;
}

interface AuditLog {
  id: string;
  action: string;
  table_name: string;
  record_id: string;
  timestamp: string;
  ip_address: string;
  old_data?: any;
  new_data?: any;
  changed_fields?: string[];
}


interface SecuritySettings {
  session_timeout_minutes: number;
  ip_whitelist: string;
  max_login_attempts: number;
  lockout_duration_minutes: number;
  is_active: boolean;
}


export function SecurityPortal({ initialTab }: { initialTab?: string }) {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [activeTab, setActiveTab] = useState(initialTab || "sessions");

  const [settings, setSettings] = useState<SecuritySettings>({
    session_timeout_minutes: 60,
    ip_whitelist: "",
    max_login_attempts: 5,
    lockout_duration_minutes: 15,
    is_active: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedLog, setExpandedLog] = useState<string | null>(null);



  const fetchData = async () => {
    try {
      setLoading(true);
      const [sessionsRes, auditRes, settingsRes] = await Promise.all([
        API.get("/security/sessions"),
        API.get("/security/audit-trail"),
        API.get("/security/settings")
      ]);
      setSessions(sessionsRes.data);
      setAuditLogs(auditRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error("Failed to fetch security data:", error);
      toast.error("Failed to sync security records");
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);


  const handleTerminateSession = async (sessionId: string) => {
    try {
      await API.delete(`/security/sessions/${sessionId}`);
      toast.success("Session terminated successfully");
      setSessions(sessions.filter(s => s.id !== sessionId));
    } catch (error) {
      toast.error("Failed to terminate session");
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      await API.patch("/security/settings", settings);
      toast.success("Security settings updated");
    } catch (error) {
      toast.error("Failed to update security settings");
    } finally {
      setSaving(false);
    }
  };


  const getDeviceIcon = (deviceType: string) => {
    switch (deviceType.toLowerCase()) {
      case "mobile": return <Smartphone className="h-5 w-5" />;
      case "tablet": return <Tablet className="h-5 w-5" />;
      default: return <Monitor className="h-5 w-5" />;
    }
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-[1400px] mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-foreground flex items-center gap-3">
              <ShieldCheck className="h-8 w-8 text-primary" />
              Security & Audit Portal
            </h1>
            <Breadcrumb items={[{ label: "Security Center" }]} className="mt-2" />
          </div>
          <Button
            variant="outline"
            onClick={fetchData}
            disabled={loading}
            className="rounded-xl border-white/10"
          >
            {loading ? "Syncing..." : "Refresh Status"}
          </Button>
        </div>

        <Tabs
          value={activeTab}
          className="space-y-6"
        >


          <TabsContent value="sessions" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sessions.map((session) => (
                <Card key={session.id} className="glass-card overflow-hidden group">
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between">
                      <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary group-hover:scale-110 transition-transform">
                        {getDeviceIcon(session.device_type)}
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <Badge variant={session.is_active ? "default" : "secondary"} className="rounded-full">
                          {session.is_active ? "Current Session" : "Inactive"}
                        </Badge>
                        {session.user_role && (
                          <Badge variant="outline" className="rounded-full text-[10px] tracking-tighter uppercase font-bold text-muted-foreground border-white/10">
                            {session.user_role}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <CardTitle className="text-lg font-bold mt-4 line-clamp-1">
                      {session.user_name || session.user_email ? (
                        <div className="flex flex-col">
                          <span className="text-base">{session.user_name || "Unknown User"}</span>
                          <span className="text-xs text-muted-foreground font-normal">{session.user_email || "No email"}</span>
                          <span className="text-xs text-muted-foreground font-normal mt-2 border-t border-white/10 pt-2">{session.user_agent}</span>
                        </div>
                      ) : (
                        session.user_agent
                      )}
                    </CardTitle>
                    <CardDescription className="flex items-center gap-2">
                      <MapPin className="h-3 w-3" />
                      {session.ip_address}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex flex-col gap-2 text-xs font-medium text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <Clock className="h-3 w-3" />
                        Last Active: {format(new Date(session.last_active), "MMM dd, HH:mm")}
                      </div>
                      <div className="flex items-center gap-2">
                        <Globe className="h-3 w-3" />
                        Signed in: {format(new Date(session.created_at), "MMM dd, yyyy")}
                      </div>
                    </div>
                    <Button
                      variant="destructive"
                      size="sm"
                      className="w-full rounded-xl gap-2 mt-4 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => handleTerminateSession(session.id)}
                    >
                      <LogOut className="h-4 w-4" />
                      Revoke Access
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="audit">
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-primary" />
                  System Activity Log
                </CardTitle>
                <CardDescription>Real-time audit of all administrative actions and security events.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="relative space-y-0 before:absolute before:inset-0 before:ml-5 before:-translate-x-px before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-white/5 before:to-transparent">
                  {auditLogs.map((log, index) => (
                    <div key={log.id} className="border-b border-white/5 last:border-0">
                      <div
                        className="relative flex items-center justify-between p-4 group cursor-pointer hover:bg-white/[0.02] transition-colors"
                        onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                      >
                        <div className="flex items-center gap-4">
                          <div className={cn(
                            "relative h-10 w-10 flex items-center justify-center rounded-full border z-10 transition-colors",
                            log.action === "FAILED_LOGIN" ? "bg-red-500/10 border-red-500/20 text-red-500" :
                              log.action === "LOGIN" ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-500" :
                                "bg-white/5 border-white/10 text-muted-foreground group-hover:bg-primary/20 group-hover:text-primary"
                          )}>
                            {log.action === "FAILED_LOGIN" ? <AlertCircle className="h-5 w-5" /> : <History className="h-5 w-5" />}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className={cn(
                                "text-sm font-bold uppercase tracking-wider",
                                log.action === "FAILED_LOGIN" ? "text-red-400" :
                                  log.action === "LOGIN" ? "text-emerald-400" : ""
                              )}>{log.action}</span>
                              <Badge variant="outline" className="text-[10px] py-0">{log.table_name}</Badge>
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {log.action === "FAILED_LOGIN" ? `Failed attempt from ${log.ip_address}` :
                                log.action === "UPDATE" ? `Modified ${log.changed_fields?.length || 0} fields` :
                                  `System action on ${log.table_name}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-right">
                            <p className="text-xs font-bold text-foreground">{format(new Date(log.timestamp), "HH:mm:ss")}</p>
                            <p className="text-[10px] text-muted-foreground uppercase font-bold tracking-widest">{format(new Date(log.timestamp), "MMM dd, yyyy")}</p>
                          </div>
                          <ChevronRight className={cn(
                            "h-4 w-4 text-muted-foreground transition-transform",
                            expandedLog === log.id ? "rotate-90" : ""
                          )} />
                        </div>
                      </div>

                      {expandedLog === log.id && (log.old_data || log.new_data) && (
                        <div className="px-4 pb-4 ml-14 space-y-3">
                          <div className="grid grid-cols-1 gap-2">
                            {log.changed_fields?.map(field => (
                              <div key={field} className="flex flex-col p-2 rounded-lg bg-white/5 border border-white/5">
                                <span className="text-[10px] font-bold text-primary uppercase tracking-tighter mb-1">{field.replace('_', ' ')}</span>
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <span className="text-[9px] text-muted-foreground uppercase">From</span>
                                    <p className="text-xs text-red-300 font-mono truncate">{String(log.old_data?.[field] ?? 'None')}</p>
                                  </div>
                                  <div>
                                    <span className="text-[9px] text-muted-foreground uppercase">To</span>
                                    <p className="text-xs text-emerald-300 font-mono truncate">{String(log.new_data?.[field] ?? 'None')}</p>
                                  </div>
                                </div>
                              </div>
                            ))}
                            {log.action === "CREATE" && log.new_data && (
                              <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/10">
                                <span className="text-[10px] font-bold text-emerald-500 uppercase mb-2 block">Initial Data</span>
                                <pre className="text-[10px] text-emerald-200/70 font-mono whitespace-pre-wrap">
                                  {JSON.stringify(log.new_data, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-[9px] text-muted-foreground pt-2 border-t border-white/5">
                            <span className="flex items-center gap-1"><MapPin className="h-2.5 w-2.5" /> {log.ip_address}</span>
                            <span className="flex items-center gap-1"><User className="h-2.5 w-2.5" /> {log.record_id}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}


                  {auditLogs.length === 0 && (
                    <div className="py-20 text-center space-y-3">
                      <History className="h-12 w-12 text-muted-foreground/20 mx-auto" />
                      <p className="text-muted-foreground/60 font-medium">No system activities recorded yet.</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="settings">
            <Card className="glass-card max-w-2xl">
              <CardHeader>
                <div className="h-12 w-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary mb-4">
                  <ShieldCheck className="h-6 w-6" />
                </div>
                <CardTitle>Protection Settings</CardTitle>
                <CardDescription>Configure advanced infrastructure security controls for your client.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="grid gap-2">
                    <label className="text-sm font-bold flex items-center gap-2">
                      <Globe className="h-4 w-4 text-blue-400" />
                      IP Whitelisting
                    </label>
                    <textarea
                      className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-sm focus:outline-none focus:border-primary/50 transition-colors min-h-[80px]"
                      placeholder="Enter comma-separated IPs (e.g. 192.168.1.1, 10.0.0.1)"
                      value={settings.ip_whitelist}
                      onChange={(e) => setSettings({ ...settings, ip_whitelist: e.target.value })}
                    />
                    <p className="text-[10px] text-muted-foreground">Leave empty to allow administrative access from all locations.</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <label className="text-sm font-bold flex items-center gap-2">
                        <Clock className="h-4 w-4 text-emerald-400" />
                        Session Timeout
                      </label>
                      <input
                        type="number"
                        className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-sm focus:outline-none focus:border-primary/50 transition-colors"
                        value={settings.session_timeout_minutes}
                        onChange={(e) => setSettings({ ...settings, session_timeout_minutes: parseInt(e.target.value) })}
                      />
                      <p className="text-[10px] text-muted-foreground">Minutes until auto-logout.</p>
                    </div>

                    <div className="grid gap-2">
                      <label className="text-sm font-bold flex items-center gap-2">
                        <AlertCircle className="h-4 w-4 text-amber-400" />
                        Failed Attempts
                      </label>
                      <input
                        type="number"
                        className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-sm focus:outline-none focus:border-primary/50 transition-colors"
                        value={settings.max_login_attempts}
                        onChange={(e) => setSettings({ ...settings, max_login_attempts: parseInt(e.target.value) })}
                      />
                      <p className="text-[10px] text-muted-foreground">Max attempts before lockout.</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 rounded-2xl bg-primary/5 border border-primary/10">
                    <div className="flex gap-4 items-center">
                      <User className="h-5 w-5 text-primary" />
                      <div>
                        <h4 className="font-bold text-sm">Active Protection</h4>
                        <p className="text-xs text-muted-foreground">Enable all security enforcements for this client.</p>
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      className="w-5 h-5 accent-primary"
                      checked={settings.is_active}
                      onChange={(e) => setSettings({ ...settings, is_active: e.target.checked })}
                    />
                  </div>
                </div>

                <Button
                  className="w-full rounded-xl gap-2 h-11"
                  onClick={handleSaveSettings}
                  disabled={saving}
                >
                  <ShieldCheck className="h-4 w-4" />
                  {saving ? "Deploying..." : "Update Security Policy"}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

        </Tabs>
      </div>
    </div>
  );
}
